import React, { createContext, useState } from "react";

// ── Context ───────────────────────────────────────────────────────────────────
//
// Write-only on desktop (MainPage + MapPage broadcast into it via static contextType).
// Mobile reads all fields via useContext(SelectionContext).
//
// Fields 1–7 (selection-input) owned by MainPage, broadcast from its handlers.
// Fields 8–10 (map-selection-output) owned by MapPage, broadcast from its
//   componentDidUpdate on prevState comparisons — same pattern as UK MapPage's
//   selectedConstituencyCode / geoLevel writes.
//
// NOT in context: demographic filters (mobile holds its own), viewMode +
//   waveOptions (derived), ActionTarget* (mobile Target owns local state),
//   geoStateFilter, ActionMode.

const SelectionContext = createContext({
  // 1 — active theme slug ("pol", "healthcare", "phrma", etc.)
  selectedSection: "pol",
  setSelectedSection: () => {},

  // 2 — active question key (e.g. "econ_approve")
  selectedVariable: null,
  setSelectedVariable: () => {},

  // 3 — colouring mode ("net", "top", "second", "third", or a key string)
  preferenceFilter: null,
  setPreferenceFilter: () => {},

  // 4 — active geographic level
  //       "states" | "counties" | "cd" | "zipcodes" |
  //       "statesenate" | "statehouse" | "media_market"
  geoLevel: "states",
  setGeoLevel: () => {},

  // 5 — selected wave number (integer) or null for non-tracking questions
  waveSelect: null,
  setWaveSelect: () => {},

  // 6 — wave-comparison mode active
  compareWaves: false,
  setCompareWaves: () => {},

  // 7 — which movement to colour in compare mode
  //       "__top__" | "__top_neg__" | "net" | <key string>
  compareKey: "__top__",
  setCompareKey: () => {},

  // 8 — 2-digit FIPS of the state the user has drilled into; null at national view
  selectedStateCode: null,
  setSelectedStateCode: () => {},

  // 9 — display name of the selected area ("Ramsey County", "TX-07", …)
  selectedAreaName: null,
  setSelectedAreaName: () => {},

  // 10 — geo level of the selected area ("counties", "cd", "zipcodes", …)
  //       null when no area is selected
  selectedAreaLevel: null,
  setSelectedAreaLevel: () => {},
});

export function SelectionProvider({ children }) {
  const [selectedSection,    setSelectedSection]    = useState("pol");
  const [selectedVariable,   setSelectedVariable]   = useState(null);
  const [preferenceFilter,   setPreferenceFilter]   = useState(null);
  const [geoLevel,           setGeoLevel]           = useState("states");
  const [waveSelect,         setWaveSelect]         = useState(null);
  const [compareWaves,       setCompareWaves]       = useState(false);
  const [compareKey,         setCompareKey]         = useState("__top__");
  const [selectedStateCode,  setSelectedStateCode]  = useState(null);
  const [selectedAreaName,   setSelectedAreaName]   = useState(null);
  const [selectedAreaLevel,  setSelectedAreaLevel]  = useState(null);

  return (
    <SelectionContext.Provider
      value={{
        selectedSection,    setSelectedSection,
        selectedVariable,   setSelectedVariable,
        preferenceFilter,   setPreferenceFilter,
        geoLevel,           setGeoLevel,
        waveSelect,         setWaveSelect,
        compareWaves,       setCompareWaves,
        compareKey,         setCompareKey,
        selectedStateCode,  setSelectedStateCode,
        selectedAreaName,   setSelectedAreaName,
        selectedAreaLevel,  setSelectedAreaLevel,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
}

export default SelectionContext;
